/**
 * <s-product-gallery> — PDP gallery: one main stage with thumbnails, arrows, and
 * variant-aware media switching.
 *
 * Required markup:
 *   [data-gallery-track]               — horizontal scroll-snap track of slides.
 *   [data-gallery-slide][data-media-id] — one per product media, in order.
 * Optional markup:
 *   [data-gallery-thumb][data-index]   — thumbnail buttons; aria-current marks the active one.
 *   button[data-gallery-prev|next]     — previous/next arrows.
 *   script[data-variant-media]         — JSON map of variant id → featured media id.
 *
 * Variant coupling: listens for `change` on the closest [data-product-detail] and
 * reads data-variant-id from the changed radio or the selected <option>, so the
 * image switches immediately without waiting for the product-form partial.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #variantMedia = {};
  #index = 0;
  #frame = 0;
  #userNav = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    this.#slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    if (!this.#track || this.#slides.length === 0) return;

    const map = this.querySelector('script[data-variant-media]');
    try {
      this.#variantMedia = map ? JSON.parse(map.textContent || '{}') : {};
    } catch {
      this.#variantMedia = {};
    }

    const initial = Number(this.dataset.initialIndex || 0);
    this.goTo(Number.isFinite(initial) ? initial : 0, 'instant');
    this.#resumeHoverVideo();

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) {
        this.goTo(Number(thumb.dataset.index), undefined, true);
        return;
      }
      if (event.target.closest('[data-gallery-prev]')) this.goTo(this.#index - 1, undefined, true);
      if (event.target.closest('[data-gallery-next]')) this.goTo(this.#index + 1, undefined, true);
    }, opts);

    // A swipe on the stage counts as the shopper choosing a slide, so a video reached that way starts playing.
    this.#track.addEventListener('pointerdown', () => { this.#userNav = true; }, { ...opts, passive: true });
    this.#track.addEventListener('touchstart', () => { this.#userNav = true; }, { ...opts, passive: true });

    this.#track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#syncFromScroll());
    }, { ...opts, passive: true });

    const scope = this.closest('[data-product-detail]') || this.parentElement;
    scope?.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement)) return;
      const variantId = input.dataset.variantId
        || (input instanceof HTMLSelectElement ? input.selectedOptions[0]?.dataset.variantId : undefined);
      if (!variantId) return;
      // A new variant choice stops any playing video, even when that variant keeps the same media.
      this.#pauseVideos();
      this.showMedia(this.#variantMedia[variantId]);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  showMedia(mediaId) {
    if (!mediaId) return;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.goTo(index);
  }

  goTo(index, behavior, autoplay = false) {
    const count = this.#slides.length;
    const next = ((index % count) + count) % count;
    const slide = this.#slides[next];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({
      left: slide.offsetLeft - this.#track.offsetLeft,
      behavior: behavior || (reduced ? 'instant' : 'smooth'),
    });
    this.#userNav = autoplay;
    this.#setActive(next, autoplay);
  }

  #syncFromScroll() {
    const width = this.#track.clientWidth;
    if (!width) return;
    const index = Math.round(this.#track.scrollLeft / width);
    if (index !== this.#index && index >= 0 && index < this.#slides.length) this.#setActive(index, this.#userNav);
  }

  // Picks up a video the shopper was previewing on a product card (see s-hover-video.js),
  // opening its slide at the same second. Only a fresh hand-off for this product counts.
  #resumeHoverVideo() {
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem('vantara:hover-video') || 'null');
      sessionStorage.removeItem('vantara:hover-video');
    } catch {
      return;
    }
    if (!saved || !saved.mediaId || Date.now() - saved.at > 20000) return;
    const handle = window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle || decodeURIComponent(handle) !== saved.handle) return;

    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(saved.mediaId));
    const video = this.#slides[index]?.querySelector('video');
    if (!video) return;

    this.goTo(index, 'instant');
    const start = () => {
      try { video.currentTime = saved.time || 0; } catch {}
      // Sound needs a tap on the new page, so the browser may only allow a muted start.
      video.play()?.catch(() => {
        video.muted = true;
        video.play()?.catch(() => {});
      });
    };
    if (video.readyState >= 1) start();
    else video.addEventListener('loadedmetadata', start, { once: true, signal: this.#ac.signal });
    if (video.readyState < 1 && video.preload === 'none') video.load();
  }

  #pauseVideos(keepIndex = -1) {
    this.#slides.forEach((slide, i) => {
      if (i === keepIndex) return;
      slide.querySelectorAll('video').forEach((video) => {
        if (!video.paused) video.pause();
      });
    });
  }

  #playVideo(index) {
    const video = this.#slides[index]?.querySelector('video');
    if (!video || !video.paused) return;
    // Browsers may refuse playback with sound outside a tap; fall back to muted so it still starts.
    video.play()?.catch(() => {
      video.muted = true;
      video.play()?.catch(() => {});
    });
  }

  #setActive(index, autoplay = false) {
    this.#index = index;
    // Leaving a slide also stops its video so it never keeps playing out of view.
    this.#pauseVideos(index);
    if (autoplay) this.#playVideo(index);
    this.#slides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== index);
    });
    this.#thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        // Scroll only the thumbnail rail, never the page.
        const rail = thumb.closest('[data-gallery-thumbs]');
        if (rail) {
          rail.scrollTo({
            left: thumb.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2,
            top: thumb.offsetTop - (rail.clientHeight - thumb.offsetHeight) / 2,
            behavior: 'smooth',
          });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
